<template>
  <div>
    <OnboardingUseCaseModal
      v-if="shouldShowOnboardingModal"
      :saving="onboardingSaving"
      @submit="saveOnboardingUseCase"
    />
    <AppUpdateNotice
      v-if="showAppUpdateNotice"
      @dismiss="dismissAppUpdateNotice"
      @refresh="refreshAppForUpdate"
    />
    <!-- Static sidebar for desktop -->
    <div>
      <!-- Off-canvas menu for mobile, show/hide based on off-canvas menu state. -->
      <div class="fixed inset-0 z-50 flex" role="dialog" aria-modal="true" v-show="mainMenuToggle">
        <!--
          Off-canvas menu overlay, show/hide based on off-canvas menu state.

          Entering: "transition-opacity ease-linear duration-300"
            From: "opacity-0"
            To: "opacity-100"
          Leaving: "transition-opacity ease-linear duration-300"
            From: "opacity-100"
            To: "opacity-0"
        -->
        <div class="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity ease-linear duration-300" aria-hidden="true"
           :class="[mainMenuToggle ? 'opacity-100' : 'opacity-0']"
        ></div>

        <!--
          Off-canvas menu, show/hide based on off-canvas menu state.

          Entering: "transition ease-in-out duration-300 transform"
            From: "-translate-x-full"
            To: "translate-x-0"
          Leaving: "transition ease-in-out duration-300 transform"
            From: "translate-x-0"
            To: "-translate-x-full"
        -->
        <div class="relative max-w-xs w-full bg-white pt-5 pb-4 flex-1 flex flex-col transition ease-in-out duration-300 transform"
           :class="{'-translate-x-full': !mainMenuToggle, 'translate-x-0': mainMenuToggle}">
          <!--
            Close button, show/hide based on off-canvas menu state.

            Entering: "ease-in-out duration-300"
              From: "opacity-0"
              To: "opacity-100"
            Leaving: "ease-in-out duration-300"
              From: "opacity-100"
              To: "opacity-0"
          -->
          <div class="absolute top-0 right-0 -mr-12 pt-2">
            <button @click="mainMenuToggle = false" type="button" class="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
              <span class="sr-only">Close sidebar</span>
              <!-- Heroicon name: outline/x -->
              <svg class="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="mt-5 flex-1 h-0 overflow-y-auto">
            <nav class="px-2 space-y-1">
              <!-- Current: "bg-gray-100 text-gray-900", Default: "text-gray-600 hover:bg-gray-50 hover:text-gray-900" -->
              <a
                @click="navChange('Tracker')"
                class="cursor-pointer text-gray-600 hover:bg-gray-50 hover:text-gray-900 group rounded-md py-2 px-2 flex items-center text-base font-medium"
                :class="[currentPage === 'Tracker' ? 'bg-gray-100' : '']"
              >
                <!--
                  Heroicon name: outline/home

                  Current: "text-gray-500", Default: "text-gray-400 group-hover:text-gray-500"
                -->
                <svg xmlns="http://www.w3.org/2000/svg" class="text-gray-500 mr-4 flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
                Board
              </a>

              <a
                @click="navChange('Blocks')"
                class="cursor-pointer text-gray-600 hover:bg-gray-50 hover:text-gray-900 group rounded-md py-2 px-2 flex items-center text-base font-medium"
                :class="[currentPage === 'Blocks' ? 'bg-gray-100' : '']"
              >
                <!-- Heroicon name: outline/view-grid -->
                <svg class="text-gray-400 group-hover:text-gray-500 mr-4 flex-shrink-0 h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                Blocks
              </a>

              <a
                @click="navChange('Projects')"
                class="cursor-pointer text-gray-600 hover:bg-gray-50 hover:text-gray-900 group rounded-md py-2 px-2 flex items-center text-base font-medium"
                :class="[currentPage === 'Projects' ? 'bg-gray-100' : '']"
              >
                <!-- Heroicon name: outline/folder -->
                <svg class="text-gray-400 group-hover:text-gray-500 mr-4 flex-shrink-0 h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                Projects
              </a>

              <a
                @click="navChange('Reports')"
                class="cursor-pointer text-gray-600 hover:bg-gray-50 hover:text-gray-900 group rounded-md py-2 px-2 flex items-center text-base font-medium"
                :class="[currentPage === 'Reports' ? 'bg-gray-100' : '']"
              >
                <!-- Heroicon name: outline/chart-bar -->
                <svg class="text-gray-400 group-hover:text-gray-500 mr-4 flex-shrink-0 h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Reports
              </a>
            </nav>
          </div>
        </div>

        <div class="flex-shrink-0 w-14">
          <!-- Dummy element to force sidebar to shrink to fit close icon -->
        </div>
      </div>

    </div>

    <div class="fixed w-full top-0 bg-white bg-opacity-50 flex z-40 justify-items-end" id="app-nav">
      <div class="flex-none flex">
          <nav class="px-2 flex" v-if="currentUser">

            <button
              type="button"
              class="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
              @click="mainMenuToggle = true"
            >
              <span class="sr-only">Open sidebar</span>
              <!-- Heroicon name: outline/menu-alt-2 -->
              <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7" />
              </svg>
            </button>

            <button
              v-show="onTracker"
              type="button"
              id="tracker-build"
              class="px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
              @click="navChange('Tracker Build')"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 14v6m-3-3h6M6 10h2a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2zm10 0h2a2 2 0 002-2V6a2 2 0 00-2-2h-2a2 2 0 00-2 2v2a2 2 0 002 2zM6 20h2a2 2 0 002-2v-2a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2z" />
              </svg>
              <span>Build</span>
            </button>
          </nav>
      </div>
      <div class="flex-grow flex">
        <div class="flex-grow"></div>

      <router-link
        v-if="showTrialIndicator"
        to="/subscription"
        class="mr-2 mt-1 inline-flex items-center rounded-xl border-[3px] px-2.5 py-1.5 text-sm font-bold leading-none whitespace-nowrap transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2"
        :class="trialIndicatorClass"
        :style="trialIndicatorStyle"
        :title="trialIndicatorTitle"
      >
        <span class="flex h-5 w-5 items-center justify-center rounded-lg border-2 border-current border-opacity-30 bg-white bg-opacity-80">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.036 3.188a1 1 0 00.95.69h3.352c.969 0 1.371 1.24.588 1.81l-2.712 1.971a1 1 0 00-.363 1.118l1.036 3.188c.3.921-.755 1.688-1.539 1.118l-2.712-1.971a1 1 0 00-1.176 0l-2.712 1.971c-.783.57-1.838-.197-1.539-1.118l1.036-3.188a1 1 0 00-.363-1.118L2.22 8.615c-.783-.57-.38-1.81.588-1.81h3.352a1 1 0 00.95-.69l1.036-3.188z" />
          </svg>
        </span>
        <span class="ml-2 hidden sm:inline">{{ trialIndicatorLabel }}</span>
        <span class="ml-2 hidden md:inline opacity-90">{{ trialIndicatorDetail }} →</span>
        <span class="ml-2 sm:hidden">{{ trialIndicatorShortLabel }}</span>
      </router-link>

      <a class="mr-1 items-center hidden md:inline-flex px-1 border border-transparent text-sm font-medium rounded-md text-red-500 bg-transparent hover:text-red-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
         href="https://www.youtube.com/watch?v=fuV8SMyMsIg"
         target="_blank"
      >
        <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path></svg>
        Watch video
      </a>
      <router-link v-if="currentUser" to="/feedback" id="appFeedback" class="mr-2 hidden md:inline-flex items-center px-1 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 bg-transparent hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
        <svg class="-ml-0.5 mr-1 h-4 w-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M18 3a1 1 0 00-1.447-.894L8.763 6H5a3 3 0 000 6h.28l1.771 5.316A1 1 0 008 18h1a1 1 0 001-1v-4.382l6.553 3.276A1 1 0 0018 15V3z" clip-rule="evenodd"></path></svg>
        Feedback?
      </router-link>
      <button v-if="currentUser" @click="IMWidgetActivate" id="appHelp" type="button" class="inline-flex mr-2 items-center px-1 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 bg-transparent hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
        <svg class="-ml-0.5 mr-1 h-4 w-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path></svg>
        Help
      </button>

      <div class="flex-shrink-0">
          <!-- Profile dropdown -->
          <div class="mr-3 mt-2 relative" v-if="currentUser">
            <div>
              <div
                engagekit-badges-unseen-activities-badge
                engagekit-badges-launcher
                class="cursor-pointer rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                <span class="sr-only">Badges</span>
                <img class="h-8" src="@/assets/badge.png"/>
              </div>
            </div>
          </div>
        </div>
        <div class="flex-shrink-0">
          <!-- Profile dropdown -->
          <div class="mr-3 mt-2 relative" v-if="currentUser">
            <div>
              <button
                @click="profileToggle = !profileToggle"
                class="bg-white rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" id="user-menu" aria-haspopup="true">
                <span class="sr-only">Open user menu</span>
                <img class="h-7 w-7 rounded-full object-cover" :src="currentUserAvatarUrl"/>
              </button>
            </div>
            <div v-if="profileToggle" class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5" role="menu" aria-orientation="vertical" aria-labelledby="user-menu">
              <router-link
                      @click="profileToggle = false"
                      to="/profile"
                      class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem"
              >Profile</router-link>
              <router-link
                      @click="profileToggle = false"
                      to="/profile-edit"
                      class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem"
              >Settings</router-link>
              <router-link
                      @click="profileToggle = false"
                      to="/subscription"
                      class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem"
              >Subscription</router-link>
              <a
                v-if="isSuperUser"
                @click="startImpersonation"
                class="cursor-pointer block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                role="menuitem"
              >Impersonate user</a>
                    <router-link
                      v-if="isSuperUser"
                      @click="profileToggle = false"
                      to="/feature-audit"
                      class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem"
                    >Feature audit</router-link>
              <a @click="logout" class="cursor-pointer block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem">Log out</a>
            </div>
          </div>

        </div>
      </div>
    </div>
    <main class="w-full" style="margin-top: 36px;">
      <router-view />
    </main>
  </div>
</template>

<script>

  import {mapGetters, mapActions} from "vuex";
  import Plausible from 'plausible-tracker';
  import {v4 as uuidv4} from "uuid";
  import dayjs from "dayjs";
  import defaultAvatar from '@/assets/avatar.png';
  import { resolveAvatarUrl } from '@/helpers/avatar';
  import { APP_VERSION } from '@/generated/app-version';
  import AppUpdateNotice from '@/components/AppUpdateNotice.vue';
  import OnboardingUseCaseModal from '@/components/OnboardingUseCaseModal.vue';
  import { getTrialDaysRemaining } from '@/helpers/trial-status';
  import { selectFromSupabase, updateInSupabase, insertIntoSupabase } from '@/helpers/supabase-api';
  import {
    LOGIN_REDIRECT_BLOCKS,
    LOGIN_REDIRECT_BOARD,
    resolveLoginRedirectPath,
  } from '@/helpers/login-redirect';

  const APP_VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000;
  const APP_VERSION_REMIND_LATER_MS = 15 * 60 * 1000;
  const APP_VERSION_SNOOZE_KEY = 'kairo-app-update-snooze-until';
  const TRIAL_STATUS_POLL_INTERVAL_MS = 15 * 60 * 1000;

  const { enableAutoPageviews } = Plausible({
    domain: 'getkairo.com',
    hashMode: true
  });
  enableAutoPageviews();

  export default {
    components: {
      AppUpdateNotice,
      OnboardingUseCaseModal,
    },
    data() {
      return {
        profileToggle: false,
        mainMenuToggle: false,
        bodyClass: '',
        freeAvailable: true,
        showSidebar: false,

        showOnboardingModal: false,
        onboardingSaving: false,
        onboardingLoadedForUserId: null,
        onboardingAnswers: null,
        defaultAvatar,
        resolvedAvatarUrl: '',
        currentAppVersion: APP_VERSION,
        latestAppVersion: null,
        showAppUpdateNotice: false,
        appUpdateSnoozedUntil: 0,
        appVersionPollId: null,
        trialStatusPollId: null,
      }
    },
    computed: {
      ...mapGetters("auth", ["currentUser", "authToken"]),
      userTrialing() {
        if (this.currentUser && this.currentUser.status === 'trialing') return true;
        return false;
      },
      userTrialExpired() {
        if (this.currentUser && this.currentUser.status === 'trial_expired') return true;
        return false;
      },
      showTrialIndicator() {
        return this.userTrialing || this.userTrialExpired;
      },
      trialEndTimestamp() {
        return this.currentUser?.app_metadata?.trial_end || null;
      },
      trialDaysRemaining() {
        if (!this.trialEndTimestamp || !this.userTrialing) return null;
        return getTrialDaysRemaining(this.trialEndTimestamp);
      },
      trialIndicatorLabel() {
        if (this.userTrialExpired) return 'Trial ended';
        if (this.trialDaysRemaining === null) return 'Trial';
        return 'Trial';
      },
      trialIndicatorDetail() {
        if (this.userTrialExpired) return 'Upgrade to keep access';
        if (this.trialDaysRemaining === null) return 'Active';
        return this.trialDaysRemaining === 0
          ? 'Ends today'
          : `${this.trialDaysRemaining} day${this.trialDaysRemaining === 1 ? '' : 's'} left`;
      },
      trialIndicatorShortLabel() {
        if (this.userTrialExpired) return 'Ended';
        if (this.trialDaysRemaining === null) return 'Active';
        return this.trialDaysRemaining === 0 ? 'Today' : `${this.trialDaysRemaining}d left`;
      },
      trialIndicatorTitle() {
        if (this.userTrialExpired) return 'Your trial has ended. Open Subscription.';
        if (this.trialDaysRemaining === null) return 'Your trial is active. Open Subscription.';
        return this.trialDaysRemaining === 0
          ? 'Your trial ends today. Open Subscription.'
          : `Your trial has ${this.trialDaysRemaining} day${this.trialDaysRemaining === 1 ? '' : 's'} left. Open Subscription.`;
      },
      trialIndicatorClass() {
        return this.userTrialExpired
          ? 'border-red-700 bg-red-100 text-red-700 hover:bg-red-50 focus:ring-red-500'
          : 'border-yellow-600 bg-yellow-200 text-yellow-800 hover:bg-yellow-100 focus:ring-yellow-400';
      },
      trialIndicatorStyle() {
        return this.userTrialExpired
          ? {
            boxShadow: 'inset 0px -6px 0px #FCA5A5, inset 0 -7px 0 0 #FEF2F2, inset 0px 1px 0px 1px #FEF2F2'
          }
          : {
            boxShadow: 'inset 0px -6px 0px #FBBF24, inset 0 -7px 0 0 #FFFBEB, inset 0px 1px 0px 1px #FFFBEB'
          };
      },
      currentStoredAvatar() {
        return this.currentUser?.avatar_url || this.currentUser?.profile?.avatar_url || '';
      },
      currentUserAvatarUrl() {
        return this.resolvedAvatarUrl || this.defaultAvatar;
      },
      isSuperUser() {
        return this.currentUser?.app_metadata?.is_super_user === true;
      },
      isConfirmEmailRoute() {
        return this.$route.name === 'Confirm email';
      },
      shouldShowOnboardingModal() {
        return this.showOnboardingModal && !this.isConfirmEmailRoute;
      },
      engageKitOnboardingUserProperties() {
        const answers = this.onboardingAnswers;
        if (!answers) {
          return {};
        }

        return {
          onboardingAnswerUseCase: answers.use_case || null,
          onboardingAnswerOtherText: answers.other_text || null,
          onboardingAnswerHopes: answers.hopes || null,
          onboardingAnswerPaymentInterest: answers.would_pay_usd_monthly ?? null,
          onboardingAnswerPaymentInterestUnsure: answers.would_pay_not_sure ?? null,
          onboardingAnswerPrimaryGoal: answers.primary_goal || null,
          onboardingAnswerPrimaryGoalChips: Array.isArray(answers.primary_goal_chips)
            ? answers.primary_goal_chips
            : null,
          onboardingAnswerPayNeed: Array.isArray(answers.pay_need)
            ? answers.pay_need
            : null,
          onboardingAnswerWinNext7Days: answers.win_next_7_days || null,
          onboardingAnswerCompletedAt: answers.completed_at || null,
        };
      },
      engageKitAuthSource() {
        const authSource = this.currentUser?.user_metadata?.auth_source
          || this.currentUser?.user_metadata?.authSource;
        if (typeof authSource === 'string' && authSource.trim()) {
          return authSource;
        }

        const provider = this.currentUser?.raw_user?.app_metadata?.provider;
        if (typeof provider === 'string' && provider.trim()) {
          return provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase();
        }

        const providers = this.currentUser?.raw_user?.app_metadata?.providers;
        if (Array.isArray(providers)) {
          const googleProvider = providers.find(value => typeof value === 'string' && value.trim());
          if (typeof googleProvider === 'string') {
            return googleProvider.charAt(0).toUpperCase() + googleProvider.slice(1).toLowerCase();
          }
        }

        return null;
      },
      canInitEngageKit() {
        if (!this.currentUser) {
          return false;
        }

        if (this.currentUser.user_metadata?.needs_provisioning === true) {
          return false;
        }

        if (this.currentUser.profile?.profile_data?.needs_provisioning === true) {
          return false;
        }

        return !!this.currentUser.app_metadata?.ek_token;
      },
      canInitInlineManual() {
        if (!this.currentUser) {
          return false;
        }

        if (this.currentUser.user_metadata?.needs_provisioning === true) {
          return false;
        }

        if (this.currentUser.profile?.profile_data?.needs_provisioning === true) {
          return false;
        }

        return true;
      },
      onTracker() {
        return this.$route.matched.some(({ name }) => name === 'Tracker');
      },
      currentPage() {
        return this.$route.name;
      }
    },
    methods: {
      ...mapActions("auth", [
        "attemptLogout",
        "fetchUserProfileFromSupabase",
        "updateUserAccount",
        "requestImpersonationLink",
      ]),
      ...mapActions(["resetEverything", "projectsResetAll"]),
      readAppUpdateSnooze() {
        const storedValue = window.localStorage.getItem(APP_VERSION_SNOOZE_KEY);
        const parsedValue = Number(storedValue || 0);
        this.appUpdateSnoozedUntil = Number.isFinite(parsedValue) ? parsedValue : 0;
      },
      writeAppUpdateSnooze(value = 0) {
        this.appUpdateSnoozedUntil = value;

        if (value > 0) {
          window.localStorage.setItem(APP_VERSION_SNOOZE_KEY, String(value));
        } else {
          window.localStorage.removeItem(APP_VERSION_SNOOZE_KEY);
        }
      },
      async fetchLatestAppVersion() {
        const response = await fetch(`/version.json?ts=${Date.now()}`, {
          cache: 'no-store',
          headers: {
            'cache-control': 'no-cache',
          },
        });

        if (!response.ok) {
          throw new Error(`Version request failed with ${response.status}`);
        }

        return response.json();
      },
      async checkForAppUpdate(options = {}) {
        const { ignoreSnooze = false } = options;

        try {
          const latestVersion = await this.fetchLatestAppVersion();

          if (!latestVersion || !latestVersion.buildId) {
            return;
          }

          if (latestVersion.buildId === this.currentAppVersion.buildId) {
            this.latestAppVersion = null;
            this.showAppUpdateNotice = false;
            this.writeAppUpdateSnooze(0);
            return;
          }

          this.latestAppVersion = latestVersion;

          if (ignoreSnooze || Date.now() >= this.appUpdateSnoozedUntil) {
            this.showAppUpdateNotice = true;
          }
        } catch (error) {
          console.warn('Failed to check app version', error);
        }
      },
      dismissAppUpdateNotice() {
        this.showAppUpdateNotice = false;
        this.writeAppUpdateSnooze(Date.now() + APP_VERSION_REMIND_LATER_MS);
      },
      refreshAppForUpdate() {
        this.showAppUpdateNotice = false;
        this.writeAppUpdateSnooze(0);
        window.location.reload();
      },
      handleAppVisibilityChange() {
        if (document.visibilityState === 'visible') {
          this.checkForAppUpdate();
        }
      },
      handleAppFocus() {
        this.checkForAppUpdate();
      },
      startAppVersionPolling() {
        this.readAppUpdateSnooze();
        this.checkForAppUpdate();

        if (this.appVersionPollId) {
          clearInterval(this.appVersionPollId);
        }

        this.appVersionPollId = window.setInterval(() => {
          this.checkForAppUpdate();
        }, APP_VERSION_POLL_INTERVAL_MS);

        window.addEventListener('focus', this.handleAppFocus);
        document.addEventListener('visibilitychange', this.handleAppVisibilityChange);
      },
      stopAppVersionPolling() {
        if (this.appVersionPollId) {
          clearInterval(this.appVersionPollId);
          this.appVersionPollId = null;
        }

        window.removeEventListener('focus', this.handleAppFocus);
        document.removeEventListener('visibilitychange', this.handleAppVisibilityChange);
      },
      async refreshTrialStatus() {
        if (!this.currentUser?.id || !this.currentUser?.app_metadata?.trial_end) {
          return;
        }

        try {
          await this.fetchUserProfileFromSupabase();
        } catch (error) {
          console.warn('Failed to refresh trial status', error);
        }
      },
      startTrialStatusPolling() {
        if (this.trialStatusPollId || !this.currentUser?.id || !this.currentUser?.app_metadata?.trial_end) {
          return;
        }

        this.trialStatusPollId = window.setInterval(() => {
          this.refreshTrialStatus();
        }, TRIAL_STATUS_POLL_INTERVAL_MS);
      },
      stopTrialStatusPolling() {
        if (this.trialStatusPollId) {
          clearInterval(this.trialStatusPollId);
          this.trialStatusPollId = null;
        }
      },
      syncTrialStatusPolling() {
        if (this.currentUser?.id && this.currentUser?.app_metadata?.trial_end) {
          this.startTrialStatusPolling();
          return;
        }

        this.stopTrialStatusPolling();
      },
      async refreshAvatarUrl(value) {
        if (!value) {
          this.resolvedAvatarUrl = '';
          return;
        }

        try {
          this.resolvedAvatarUrl = await resolveAvatarUrl(value);
        } catch (error) {
          console.warn('Failed to resolve avatar URL', error);
          this.resolvedAvatarUrl = '';
        }
      },
      async fetchOnboardingStatus() {
        if (!this.currentUser || !this.currentUser.id) {
          this.showOnboardingModal = false;
          this.onboardingLoadedForUserId = null;
          this.onboardingAnswers = null;
          return;
        }

        // Avoid refetching for the same logged-in user.
        if (this.onboardingLoadedForUserId === this.currentUser.id) {
          return;
        }

        this.onboardingLoadedForUserId = this.currentUser.id;

        try {
          const rows = await selectFromSupabase(
            'onboarding_answers',
            'use_case,other_text,hopes,would_pay_usd_monthly,would_pay_not_sure,primary_goal,primary_goal_chips,pay_need,win_next_7_days,completed_at'
          );
          const answerRow = Array.isArray(rows) ? rows[0] : null;
          this.onboardingAnswers = answerRow;

          // Show only if onboarding not completed.
          this.showOnboardingModal = !answerRow || !answerRow.use_case;
          this.initializeEngageKit();
        } catch (e) {
          // If the read fails, don't block the app.
          this.showOnboardingModal = false;
          this.onboardingAnswers = null;
          console.warn('Failed to load onboarding status', e);
        }
      },
      async saveOnboardingUseCase(payload) {
        if (!this.currentUser || !this.currentUser.id) return;
        if (!payload || !payload.useCase) return;

        const loginRedirect = payload.useCase === 'notion_blocks'
          ? LOGIN_REDIRECT_BLOCKS
          : LOGIN_REDIRECT_BOARD;

        this.onboardingSaving = true;
        try {
          const updateResult = await updateInSupabase(
              'onboarding_answers',
              {
                use_case: payload.useCase,
                other_text: payload && typeof payload.otherText !== 'undefined' ? payload.otherText : null,
                hopes: payload && typeof payload.hopes !== 'undefined' ? payload.hopes : null,
                would_pay_usd_monthly: payload && typeof payload.wouldPayUsdMonthly !== 'undefined' ? payload.wouldPayUsdMonthly : null,
                completed_at: new Date().toISOString(),
              },
              { user_id: this.currentUser.id }
          );

          // If no row existed yet, insert one.
          if (!Array.isArray(updateResult) || updateResult.length === 0) {
            await insertIntoSupabase('onboarding_answers', {
              use_case: payload.useCase,
              other_text: payload && typeof payload.otherText !== 'undefined' ? payload.otherText : null,
              hopes: payload && typeof payload.hopes !== 'undefined' ? payload.hopes : null,
              would_pay_usd_monthly: payload && typeof payload.wouldPayUsdMonthly !== 'undefined' ? payload.wouldPayUsdMonthly : null,
              completed_at: new Date().toISOString(),
            });
          }

          this.onboardingAnswers = {
            ...(this.onboardingAnswers || {}),
            use_case: payload.useCase,
            other_text: payload && typeof payload.otherText !== 'undefined' ? payload.otherText : null,
            hopes: payload && typeof payload.hopes !== 'undefined' ? payload.hopes : null,
            would_pay_usd_monthly: payload && typeof payload.wouldPayUsdMonthly !== 'undefined' ? payload.wouldPayUsdMonthly : null,
            completed_at: new Date().toISOString(),
          };

          await this.updateUserAccount({
            data: {
              login_redirect: loginRedirect,
            },
          });

          this.showOnboardingModal = false;

          // Notion blocks use-case: send the user straight to the block builder.
          if (payload.useCase === 'notion_blocks') {
            this.$router.push({ path: '/blocks/new', query: { useCase: 'notion_blocks', embedHelp: '1' } });
          } else if (payload.useCase === 'kairo_board') {
            this.$router.push({ path: '/' });
          }
        } catch (e) {
          console.error('Failed to save onboarding use case', e);
          // Keep modal open; the modal shows its own validation errors, but API errors are logged.
        } finally {
          this.onboardingSaving = false;
        }
      },
      async startImpersonation() {
        this.profileToggle = false;

        if (!this.isSuperUser) {
          return;
        }

        const input = window.prompt('Enter the email to impersonate:');
        if (input === null) {
          return;
        }

        const email = String(input).trim().toLowerCase();
        if (!email) {
          window.alert('Please provide an email.');
          return;
        }

        try {
          const actionLink = await this.requestImpersonationLink(email);
          window.location.assign(actionLink);
        } catch (error) {
          console.error('Failed to start impersonation', error);
          window.alert(error?.message || 'Unable to start impersonation.');
        }
      },
      logout() {
        this.profileToggle = false;
        this.attemptLogout()
            .then(resp => {
              this.resetEverything();
              this.projectsResetAll();

              this.showOnboardingModal = false;
              this.onboardingLoadedForUserId = null;

              if (typeof window.InlineManual !== 'undefined') {
                window.InlineManual("shutdown");
                window.InlineManual("boot", {
                  uid: 'anon-' + uuidv4()
                });
              }

              if (typeof window.EngageKit !== 'undefined') {
                window.EngageKit.reset();
              }

              window.location.assign('/login');
              console.log("logged out", resp);
            })
            .catch(error => {
              location.reload();
              console.error("Problem with logout", error);
            });
      },
      navChange(path) {
        this.showSidebar = false;
        this.mainMenuToggle = false;
        this.$router.push({name: path});
      },
      updateBG(className) {
        this.bodyClass = className;
      },
      IMTopicActivate(topicId) {
        if (!this.canInitInlineManual || typeof window.inline_manual_player === 'undefined') {
          return;
        }

        if (window.inline_manual_player.getCurrentTopic() === "90529") {
          window.inline_manual_player.deactivate();
        } else {
          window.inline_manual_player.activateTopic(topicId);
        }
      },
      IMWidgetActivate() {
        if (!this.canInitInlineManual || typeof window.inline_manual_player === 'undefined') {
          return;
        }

        window.inline_manual_player.togglePanel();
      },
      initializeInlineManual() {
        if (typeof window.InlineManual === 'undefined' || !this.canInitInlineManual) {
          return;
        }

        window.InlineManual("shutdown");
        window.InlineManual("boot", {
          uid: this.currentUser.id,
          plan: this.currentUser.app_plan,
          name: this.currentUser.user_metadata.full_name,
          created: dayjs(this.currentUser.created_at).unix()
        });
      },
      initializeEngageKit() {
        if (typeof window.EngageKit === 'undefined' || !this.canInitEngageKit) {
          return;
        }

        window.EngageKit.ready(function () {
          window.EngageKit.setTheme("green");
        });

        let trialEndAt = null;

        if (this.currentUser.app_metadata.trial_end) {
          trialEndAt = dayjs.unix(this.currentUser.app_metadata.trial_end).toISOString();
        }

        const userContext = {
          userId: this.currentUser.id,
          userIdentityVerificationHash: this.currentUser.app_metadata.ek_token,
          userProperties: {
            appVersion: 2,
            createdAt: this.currentUser.created_at,
            trialEndAt: trialEndAt,
            subscriptionPlan: this.currentUser.app_plan,
            subscriptionStatus: this.currentUser.status,
            subscriptionPlanDuration: this.currentUser.app_plan_duration,
            email: this.currentUser.email,
            authSource: this.engageKitAuthSource,
            ...this.engageKitOnboardingUserProperties,
          },
          reservedUserProperties: {
            name: this.currentUser.user_metadata.full_name
          }
        }

        window.EngageKit.init(
          "client_L1KhvwrU03aUnh3C8yYtEUCEmhjZQa2K4tvRJoxW3C4Tlfk1",
          userContext
        );
      },
      redirectConfirmedUserFromConfirmEmail() {
        if (!this.currentUser || !this.isConfirmEmailRoute || this.authToken) {
          return;
        }

        this.$router.replace({ path: resolveLoginRedirectPath(this.currentUser) });
      }
    },
    watch: {
      currentUser: {
        immediate: true,
        handler() {
          this.redirectConfirmedUserFromConfirmEmail();
          this.syncTrialStatusPolling();
          this.fetchOnboardingStatus();
          this.initializeInlineManual();
          this.initializeEngageKit();
          this.refreshAvatarUrl(this.currentStoredAvatar);
        }
      },
      onboardingAnswers: {
        deep: true,
        handler() {
          this.initializeEngageKit();
        }
      },
      currentStoredAvatar: {
        immediate: true,
        handler(value) {
          this.refreshAvatarUrl(value);
        }
      },
      '$route.name'() {
        this.redirectConfirmedUserFromConfirmEmail();
      },
      authToken() {
        this.redirectConfirmedUserFromConfirmEmail();
      }
    },
    mounted() {
      this.initializeInlineManual();
      this.initializeEngageKit();
      this.startAppVersionPolling();
      this.syncTrialStatusPolling();
    },
    beforeUnmount() {
      this.stopAppVersionPolling();
      this.stopTrialStatusPolling();
    },
  };
</script>
<style>
  .bg-grad-1 {
    background-color: #4158D0;
    background-image: linear-gradient(43deg, #4158D0 0%, #C850C0 46%, #FFCC70 100%);
  }
  .bg-grad-2 {
    background-color: #0093E9;
    background-image: linear-gradient(160deg, #0093E9 0%, #80D0C7 100%);
  }
  .bg-grad-3 {
    background-color: #74EBD5;
    background-image: linear-gradient(90deg, #74EBD5 0%, #9FACE6 100%);
  }
  .bg-grad-4 {
    background-color: #FBAB7E;
    background-image: linear-gradient(62deg, #FBAB7E 0%, #F7CE68 100%);
  }
  .bg-grad-5 {
    background-color: #85FFBD;
    background-image: linear-gradient(45deg, #85FFBD 0%, #FFFB7D 100%);
  }
  .bg-grad-6 {
    background-color: #8BC6EC;
    background-image: linear-gradient(135deg, #8BC6EC 0%, #9599E2 100%);
  }
  .bg-grad-7 {
    background-color: #A9C9FF;
    background-image: linear-gradient(180deg, #A9C9FF 0%, #FFBBEC 100%);
  }
  .bg-grad-8 {
    background-color: #FF3CAC;
    background-image: linear-gradient(225deg, #FF3CAC 0%, #784BA0 50%, #2B86C5 100%);
  }
</style>
